import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "wornwell_session_token";

export function AuthCallback() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        setError("No session ID found");
        setTimeout(() => navigate("/"), 2000);
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      // Check for referral code in localStorage
      const referralCode = localStorage.getItem("wornwell_referral_code");

      try {
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ 
            session_id: sessionId,
            referral_code: referralCode || null
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("Auth response error:", response.status, errorData);
          throw new Error(errorData.detail || "Authentication failed");
        }

        const userData = await response.json();
        
        // Save session token to localStorage for Safari/mobile compatibility
        if (userData.session_token) {
          localStorage.setItem(TOKEN_KEY, userData.session_token);
          // Remove token from user data before storing in state
          delete userData.session_token;
        }
        
        updateUser(userData);
        
        // Clear referral code from localStorage after use
        localStorage.removeItem("wornwell_referral_code");
        
        // Clear hash and navigate to dashboard with user data
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: userData }, replace: true });
      } catch (err) {
        console.error("Auth error:", err);
        setError("Authentication failed. Redirecting...");
        setTimeout(() => navigate("/"), 2000);
      }
    };

    processAuth();
  }, [navigate, updateUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="text-center">
          <p className="text-destructive font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-sand-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
}
